/* =====================================================================
   BARRIER ACCESS — "SA Request a Quote" app embed compatibility shim
   ---------------------------------------------------------------------
   The app fetches product data for the products on the page in batches
   of 20 handles. When a Storefront API token is configured in the app,
   each batch response REPLACES its product registry
   (GRFQConfigs.products = batch) instead of merging, so on any page
   with more than 20 product links only the last batch survives. Every
   other product's "Add to Quote" button then throws
   "Cannot read properties of undefined (reading 'id')" in the app's
   quoteData() and nothing is added to the quote.

   The app dispatches `samiGetProductList` after each batch. This shim
   listens for it and re-merges every batch into GRFQConfigs.products
   (union by product id), which restores the click behaviour. It touches
   only the app's own documented global and does nothing when the app
   embed is absent.

   Remove once Samita fixes the replace-vs-merge behaviour upstream.
   ===================================================================== */
(function () {
  'use strict';

  var registry = Object.create(null);

  function remember(list) {
    if (!list || !list.length) return;
    for (var i = 0; i < list.length; i++) {
      var product = list[i];
      if (product && product.id) registry[product.id] = product;
    }
  }

  document.addEventListener('samiGetProductList', function (event) {
    if (!window.GRFQConfigs) return;

    remember(event.detail && event.detail.products);
    remember(window.GRFQConfigs.products);

    var merged = [];
    for (var id in registry) merged.push(registry[id]);
    window.GRFQConfigs.products = merged;
  });
})();

/* =====================================================================
   BARRIER ACCESS - quote page product images (client feedback, Oct 2026)
   ---------------------------------------------------------------------
   On the app's quote page (GRFQConfigs.rfq_page, "request-for-quote"):
   - products with no image get an <img> whose src is empty or not a real
     image, which showed as a broken image plus stray text. Such rows get
     `.ba-qimg--missing` on the image frame; ba-product-card-overrides.css
     hides the broken <img> and shows a small "No image" label instead.
   - the app appends "?width=72&height=72" to every image's alt text
     (e.g. "SU3000 ...?width=72&height=72"); that suffix is stripped so
     screen readers and broken-image fallbacks read the product name only.
   The app renders and re-renders the list client-side (quantity changes,
   removals, search-to-add), so new images are handled through a
   MutationObserver. Runs on the quote page only; does nothing elsewhere.
   ===================================================================== */
(function () {
  'use strict';

  var LABEL = 'No image';

  function onQuotePage() {
    var page = (window.GRFQConfigs && window.GRFQConfigs.rfq_page) || 'request-for-quote';
    return window.location.pathname.indexOf('/pages/' + page) !== -1;
  }

  function markMissing(img) {
    var frame = img.closest('.cartitem__img-container') || img.parentElement;
    if (!frame) return;
    frame.classList.add('ba-qimg--missing');
    frame.setAttribute('data-ba-missing-label', LABEL);
  }

  function looksMissing(img) {
    var src = (img.getAttribute('src') || '').trim();
    if (!src) return true;
    if (/^(null|undefined|false)(\?|$)/i.test(src)) return true;
    // a src that is not a URL (e.g. "?width=72") resolves against the page
    if (!/^(https?:)?\/\//i.test(src) && src.charAt(0) !== '/') return true;
    return img.complete && img.naturalWidth === 0;
  }

  function fix(img) {
    if (img.dataset.baQimg) return;
    img.dataset.baQimg = '1';
    var alt = img.getAttribute('alt');
    if (alt) img.setAttribute('alt', alt.replace(/\?width=\d+&(amp;)?height=\d+$/, ''));
    if (looksMissing(img)) {
      markMissing(img);
    } else {
      img.addEventListener('error', function () { markMissing(img); }, { once: true });
    }
  }

  function scan(root) {
    var imgs = (root || document).querySelectorAll('img.grfq-product-image');
    for (var i = 0; i < imgs.length; i++) fix(imgs[i]);
  }

  function start() {
    if (!onQuotePage()) return;
    scan(document);
    var main = document.querySelector('main') || document.body;
    new MutationObserver(function () { scan(main); }).observe(main, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/* =====================================================================
   BARRIER ACCESS - empty quote page (QA bug, Nov 2026)
   ---------------------------------------------------------------------
   The header, footer and homepage "Request a Quote" buttons all open the
   app's quote page. With nothing added yet the app shows only "Your quote
   is currently empty." and a Continue Shopping link to the myshopify
   domain, so a buyer with a general request hit a dead end. Under that
   message: a line pointing to the categories and to the contact page's
   quote request form, and Continue Shopping kept on the store's own domain.
   Quote page only; does nothing once products are in the quote.
   ===================================================================== */
(function () {
  'use strict';

  var CATEGORIES_URL = '/collections';
  var CONTACT_URL = '/pages/frontpage';

  function onQuotePage() {
    var page = (window.GRFQConfigs && window.GRFQConfigs.rfq_page) || 'request-for-quote';
    return window.location.pathname.indexOf('/pages/' + page) !== -1;
  }

  function link(href, text) {
    var a = document.createElement('a');
    a.href = href;
    a.textContent = text;
    return a;
  }

  function addHelp(empty) {
    if (empty.querySelector('.ba-rfq-empty-help')) return;

    var shopping = empty.querySelector('.grfq-continue-shopping a');
    if (shopping && /\/collections\/all$/.test(shopping.getAttribute('href') || '')) {
      shopping.setAttribute('href', '/collections/all');
    }

    var help = document.createElement('p');
    help.className = 'ba-rfq-empty-help';
    help.appendChild(document.createTextNode('Add products with their Request a Quote button, '));
    help.appendChild(link(CATEGORIES_URL, 'browse categories'));
    help.appendChild(document.createTextNode(', or '));
    help.appendChild(link(CONTACT_URL, 'send us a general quote request'));
    help.appendChild(document.createTextNode('.'));
    empty.appendChild(help);
  }

  function scan() {
    var empties = document.querySelectorAll('.grfq-quote-empty');
    for (var i = 0; i < empties.length; i++) addHelp(empties[i]);
  }

  function start() {
    if (!onQuotePage()) return;
    scan();
    var main = document.querySelector('main') || document.body;
    new MutationObserver(scan).observe(main, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/* =====================================================================
   BARRIER ACCESS - quote form autofill + required fields (QA a11y, Nov 2026)
   ---------------------------------------------------------------------
   The app's quote form marks required fields with "*" in the label only
   and gives no autocomplete hints. Starred fields get aria-required so
   screen readers announce them (the app keeps doing its own validation,
   so `required` itself is not added), and the contact / shipping fields
   get the standard autocomplete tokens so browsers can fill them in.
   Quote page only; re-applied when the app re-renders the form.
   ===================================================================== */
(function () {
  'use strict';

  var AUTOCOMPLETE = {
    'contact_info[first_name]': 'given-name',
    'contact_info[last_name]': 'family-name',
    'contact_info[email]': 'email',
    'contact_info[phone]': 'tel',
    'shipping_address[country]': 'shipping country',
    'shipping_address[first_name]': 'shipping given-name',
    'shipping_address[last_name]': 'shipping family-name',
    'shipping_address[company]': 'shipping organization',
    'shipping_address[address1]': 'shipping address-line1',
    'shipping_address[address2]': 'shipping address-line2',
    'shipping_address[city]': 'shipping address-level2',
    'shipping_address[province]': 'shipping address-level1',
    'shipping_address[zip]': 'shipping postal-code',
    'shipping_address[phone]': 'shipping tel'
  };

  function onQuotePage() {
    var page = (window.GRFQConfigs && window.GRFQConfigs.rfq_page) || 'request-for-quote';
    return window.location.pathname.indexOf('/pages/' + page) !== -1;
  }

  function labelFor(field) {
    if (field.id) {
      var byFor = document.querySelector('label[for="' + field.id.replace(/"/g, '\\"') + '"]');
      if (byFor) return byFor;
    }
    return field.closest('label') || (field.parentElement && field.parentElement.querySelector('label'));
  }

  function patch() {
    var fields = document.querySelectorAll('main input[name], main textarea[name], main select[name]');
    for (var i = 0; i < fields.length; i++) {
      var field = fields[i];
      if (field.type === 'hidden' || field.dataset.baQform) continue;
      field.dataset.baQform = '1';

      var token = AUTOCOMPLETE[field.name];
      if (token && !field.getAttribute('autocomplete')) field.setAttribute('autocomplete', token);

      var label = labelFor(field);
      if (label && /\*\s*$/.test(label.textContent || '')) field.setAttribute('aria-required', 'true');
    }
  }

  function start() {
    if (!onQuotePage()) return;
    patch();
    var main = document.querySelector('main') || document.body;
    new MutationObserver(patch).observe(main, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
